import * as z from 'zod';

import { LosId } from '../../BrandedIds';

/**
 * A single parsed record from a LERETA "Customer Audit" file (a fixed-width
 * 150-byte layout). The byte-offset parser lives in
 * `packages/shared-node/src/fileImport/lereta` and produces the raw,
 * field-name-keyed object this schema validates.
 *
 * The audit file carries NO tax amounts — it is a payee/agency + contract-number
 * assignment pass. Tax amounts arrive via a separate file. Only the fields the
 * importer actually consumes are modeled here; layout-only fields (service
 * bureau number, hit code, filler, the outbound change-* fields) are dropped.
 */
export const LeretaAuditRecord = z.object({
  // Customer Number (pos 1) — LERETA customer id; informational.
  customerNumber: z.string().trim().optional(),
  // Loan ID (pos 8) — the servicer's loan number; our LosId. The match key (with taxId).
  losId: z
    .string()
    .trim()
    .min(1, 'Loan ID is required')
    .transform((v) => v as LosId),
  // Service Type (pos 34) — C = escrowed/impound, B = delinquency (non-escrowed).
  // B rows are ignored by the importer; processed (C) rows create escrowed lines.
  serviceType: z.string().trim().optional(),
  // Contract Number (pos 35) — LERETA tax-service contract number.
  contractNumber: z
    .string()
    .trim()
    .optional()
    .transform((v) => (v === '' ? undefined : v)),
  // Agency ID (pos 48) — the taxing agency identifier; our Payee.externalPayeeId.
  agencyId: z.string().trim().min(1, 'Agency ID is required'),
  // Agency Type (pos 57) — LERETA tax-type code; informational (taxType is sourced from the payee).
  agencyType: z
    .string()
    .trim()
    .optional()
    .transform((v) => (v === '' ? undefined : v)),
  // Tax ID (pos 59) — parcel number / APN; the match key against TaxEscrowSetting.apn.
  taxId: z.string().trim().min(1, 'Tax ID (APN) is required'),
  // Lien Seq. No. (pos 110) — maps to "Tax N"; informational only.
  lienSeqNo: z
    .string()
    .trim()
    .optional()
    .transform((v) => (v === '' ? undefined : v)),
});

export type LeretaAuditRecord = z.infer<typeof LeretaAuditRecord>;
